import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import {
  excessBaggageVoidAction,
  excessBaggageVoidActionModal,
  registerBaggagePinPadVoid,
} from '../../../../actions';
import { postTotalPosVoid } from '../../../../actions/totalpos/Totalpos';
import SaveButton from '../../../common/button/SaveButton';
import { numberFormatter } from '../../../../utils/number';
import printTotalPosVoid from '../../../../utils/printers/TotalPosVoid';

const BaggageVoidButton = ({
  dispatchExcessBaggageVoidActionModal,
  baggageId,
  dispatchExcessBaggageVoidAction,
  dispatchRegisterBaggagePinPadVoid,
  dispatchPostTotalPosVoid,
  user: { id: userId },
}) => {
  const [loading, setLoading] = useState(false);
  const [uri, setUri] = useState(null);
  const [message, setMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pinPadTransactionList, setPinPadTransactionList] = useState([]);
  const [pinPadProcessing, setPinPadProcessing] = useState({});
  // kept only so the cashier can (re)print the void voucher on demand - the
  // backend's pinpad transaction list doesn't carry the printing fields,
  // those only exist in the terminal's own response
  const [pinPadVoidResponses, setPinPadVoidResponses] = useState({});

  const allPinPadTransactionsVoided = pinPadTransactionList.every(
    (transaction) => transaction.voided,
  );

  const refreshPinPadTransactionList = async () => {
    const refreshed = await dispatchExcessBaggageVoidActionModal({
      baggageId,
    });
    setPinPadTransactionList(
      refreshed && refreshed.pinPadTransactionList
        ? refreshed.pinPadTransactionList
        : [],
    );
  };

  const handleClick = async () => {
    setLoading(true);
    const response = await dispatchExcessBaggageVoidActionModal({ baggageId });

    if (response) {
      setUri(response.uri);
      const { isWithAdmin } = response;
      setMessage(
        isWithAdmin
          ? '¿Confirma que desea anular el exceso de equipaje? Esta acción requiere autorización de un administrador.'
          : '¿Confirma que desea anular el exceso de equipaje?',
      );
      setPinPadTransactionList(response.pinPadTransactionList || []);
      setPinPadProcessing({});
      setPinPadVoidResponses({});
      setShowModal(true);
    }

    setLoading(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setLoading(false);
    setUri(null);
    setMessage(null);
  };

  // the charge is reversed on the pinpad first and registered with the
  // backend right away (rather than waiting for the final "Confirmar"), so a
  // card is never left charged if the user closes the modal mid-flow - the
  // backend then blocks the final baggage void until every card is voided
  const handleVoidPinPadTransaction = async (transaction) => {
    setPinPadProcessing((prev) => ({ ...prev, [transaction.id]: true }));

    const response = await dispatchPostTotalPosVoid({
      financialReference: transaction.financialReference,
      operator: String(userId),
      amount: parseFloat(transaction.amount),
      qrPayment: transaction.paymentMethod === 'QR',
    });

    if (!response) {
      setPinPadProcessing((prev) => {
        const next = { ...prev };
        delete next[transaction.id];
        return next;
      });
      return;
    }

    setPinPadVoidResponses((prev) => ({ ...prev, [transaction.id]: response }));

    try {
      await dispatchRegisterBaggagePinPadVoid({
        baggageId,
        originalTransactionId: transaction.id,
        operation: 'VOID',
        approved: Boolean(response.approved),
        responseCode: response.responseCode,
        response,
      });
    } catch ({ message: errorMessage }) {
      toastr.error('Error', errorMessage);
    }

    await refreshPinPadTransactionList();
    setPinPadProcessing((prev) => {
      const next = { ...prev };
      delete next[transaction.id];
      return next;
    });
  };

  const handlePrintPinPadVoid = (transaction) => {
    const response = pinPadVoidResponses[transaction.id];
    if (!response) return;
    printTotalPosVoid(response, transaction.paymentMethod === 'QR');
  };

  const onClickDelete = () => {
    if (!allPinPadTransactionsVoided) {
      return;
    }

    dispatchExcessBaggageVoidAction({ uri, baggageId });
    closeModal();
  };

  return (
    <>
      <Button color="danger" disabled={loading} onClick={handleClick}>
        <i className="fa fa-ban" /> Anular
      </Button>
      <Modal isOpen={showModal}>
        <ModalHeader>Confirmación de Anulación</ModalHeader>
        <ModalBody>
          {pinPadTransactionList.length > 0 && (
            <div className="mb-3">
              <p>
                Se debe anular el cobro en el POS de cada tarjeta antes de
                confirmar la anulación del equipaje:
              </p>
              <ul className="list-unstyled">
                {pinPadTransactionList.map((transaction) => {
                  const processing = Boolean(pinPadProcessing[transaction.id]);
                  return (
                    <li key={transaction.id} className="mb-2">
                      {`${transaction.cardApplication || ''} ${
                        transaction.cardNumber || ''
                      } - ${numberFormatter({
                        style: 'currency',
                        value: transaction.amount,
                      })}`}{' '}
                      {transaction.voided ? (
                        <>
                          <span className="text-success">
                            <i className="fa fa-check" /> Anulado
                          </span>{' '}
                          {pinPadVoidResponses[transaction.id] && (
                            <Button
                              size="sm"
                              color="secondary"
                              onClick={() => handlePrintPinPadVoid(transaction)}
                            >
                              <i className="fa fa-print" /> Imprimir
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button
                          size="sm"
                          color="danger"
                          disabled={processing}
                          onClick={() =>
                            handleVoidPinPadTransaction(transaction)
                          }
                        >
                          <i
                            className={`fa fa-${
                              processing ? 'spinner fa-spin' : 'credit-card'
                            }`}
                          />{' '}
                          Anular en POS
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <p>{message}</p>
        </ModalBody>
        <ModalFooter>
          <SaveButton
            action={onClickDelete}
            disabled={!allPinPadTransactionsVoided}
            text="Confirmar"
          />{' '}
          <Button color="secondary" onClick={closeModal}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

BaggageVoidButton.propTypes = {
  dispatchExcessBaggageVoidActionModal: PropTypes.func.isRequired,
  baggageId: PropTypes.number.isRequired,
  dispatchExcessBaggageVoidAction: PropTypes.func.isRequired,
  dispatchRegisterBaggagePinPadVoid: PropTypes.func.isRequired,
  dispatchPostTotalPosVoid: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  user: authentication.get('user'),
});

const mapDispatchToProps = {
  dispatchExcessBaggageVoidActionModal: excessBaggageVoidActionModal,
  dispatchExcessBaggageVoidAction: excessBaggageVoidAction,
  dispatchRegisterBaggagePinPadVoid: registerBaggagePinPadVoid,
  dispatchPostTotalPosVoid: postTotalPosVoid,
};

export default connect(mapStateToProps, mapDispatchToProps)(BaggageVoidButton);
